import { AxiosError } from 'axios'
import { makeAutoObservable, runInAction } from 'mobx'
import { showToast } from 'shared/ui'
import { logger } from 'shared/lib'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { BillingApi } from 'entities/Billing'
import { getZipCodeErrorMessageByCountry } from 'pages/settings/lib/businessAddress'
import { BusinessAddressFormStore } from 'pages/settings/ui/BusinessAddressForm'

export class BillingAddressStore {
  private _useComplianceAddress = false
  private _addressFormStore: BusinessAddressFormStore
  private _loading = false
  private _saving = false
  private _isPopulating = false

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)
    this._addressFormStore = new BusinessAddressFormStore({
      onFieldChange: this.handleFieldChange,
    })
  }

  init = async () => {
    this._loading = true

    try {
      await Promise.all([
        this._entities.billingStore.fetchBillingAddress(),
        this._addressFormStore.init(),
      ])
      this._useComplianceAddress = this.isBusinessInformationFilled
        ? Boolean(this.billingAddress?.use_business_address)
        : false

      this.populateFromBillingAddress()
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  private populateFromBillingAddress = () => {
    const response = this.billingAddress

    if (!response) return

    this._isPopulating = true

    const address = this._useComplianceAddress
      ? response['10dlc_address']
      : response.billing_address

    if (address) {
      this._addressFormStore.setAddressData({
        city: address.city || '',
        ein: '',
        ein_formatted: '',
        name: '',
        state: address.state || '',
        street1: address.line1 || '',
        street2: address.line2 || '',
        zip: address.postal_code || '',
      })

      if (address.country) {
        this._addressFormStore.setActiveCountry(address.country, false)
      }
    }

    this._isPopulating = false
  }

  get loading() {
    return this._loading || this._addressFormStore.loading
  }

  get isBusinessInformationFilled() {
    const businessInformation = this.billingAddress?.['10dlc_address']

    if (!businessInformation) return false

    return (
      Boolean(businessInformation.city) &&
      Boolean(businessInformation.country) &&
      Boolean(businessInformation.line1)
    )
  }

  get tollFreeProfile() {
    const tollFree = this._entities.complianceStore.organizationTollFree
    if (!tollFree) return null

    const profiles = tollFree.items

    return profiles.length > 0 ? profiles[0] : null
  }

  get hasAddress() {
    const response = this.billingAddress

    if (response && !response.use_business_address && !response.billing_address) return false

    return true
  }

  get useComplianceAddress() {
    return this._useComplianceAddress
  }

  get addressFormStore() {
    return this._addressFormStore
  }

  get billingAddress() {
    return this._entities.billingStore.billingAddress
  }

  get isAddressFilled() {
    return this._addressFormStore.isAddressFilled
  }

  get saving() {
    return this._saving
  }

  save = async (): Promise<boolean> => {
    if (this._saving) return false

    const addressData = this._addressFormStore.addressData
    if (!addressData) return false

    this._saving = true

    try {
      const billingAddressData = {
        city: addressData.city,
        country: this._addressFormStore.activeCountryId,
        line1: addressData.street1,
        line2: addressData.street2,
        postal_code: addressData.zip,
        state: addressData.state,
        use_business_address: this._useComplianceAddress,
      }

      await BillingApi.updateBillingAddress(billingAddressData)

      const currentResponse = this._entities.billingStore.billingAddress
      this._entities.billingStore.setBillingAddress({
        use_business_address: this._useComplianceAddress,
        '10dlc_address': currentResponse?.['10dlc_address'] ?? null,
        billing_address: {
          city: addressData.city,
          country: this._addressFormStore.activeCountryId,
          line1: addressData.street1,
          line2: addressData.street2,
          postal_code: addressData.zip,
          state: addressData.state,
        },
      })

      showToast({
        type: 'success',
        title: 'Billing address saved',
      })

      return true
    } catch (e) {
      logger.error(e)

      if (e instanceof AxiosError) {
        if (e.errors.getError('postal_code') || e.errors.getError('zip_code')) {
          const errorMessage = getZipCodeErrorMessageByCountry(
            this._addressFormStore.activeCountryId
          )
          this._addressFormStore.setZipLocalErrorMessage(errorMessage)
          return false
        }
      }

      return false
    } finally {
      runInAction(() => {
        this._saving = false
      })
    }
  }

  setUseComplianceAddress = (value: boolean) => {
    this._useComplianceAddress = value

    this.populateFromBillingAddress()
  }

  private handleFieldChange = () => {
    if (this._isPopulating) return

    if (this._useComplianceAddress) {
      this._useComplianceAddress = false
    }
  }
}
